import React, { useEffect, useState, useCallback } from 'react';
import MoneyDisplay from './MoneyDisplay';
import MoneyDisplayCount from './MoneyDisplayCount';
import CustomBtn from 'components/fragments/CustomBtn';
import CustomInput from 'components/fragments/CustomInput';
import { numberWithTwoDecimals } from 'utils/numberFormat';

const initRowValues = [
  {
    display: {
      value: 1000,
      isBankNote: true
    },
    displayCount: {
      countValue: 0,
      currentValue: 0,
      isActive: false
    }
  },
  {
    display: {
      value: 500,
      isBankNote: true
    },
    displayCount: {
      countValue: 0,
      currentValue: 0,
      isActive: false
    }
  },
  {
    display: {
      value: 100,
      isBankNote: true
    },
    displayCount: {
      countValue: 0,
      currentValue: 0,
      isActive: false
    }
  },
  {
    display: {
      value: 50,
      isBankNote: false
    },
    displayCount: {
      countValue: 0,
      currentValue: 0,
      isActive: false
    }
  },
  {
    display: {
      value: 20,
      isBankNote: false
    },
    displayCount: {
      countValue: 0,
      currentValue: 0,
      isActive: false
    }
  },
  {
    display: {
      value: 10,
      isBankNote: false
    },
    displayCount: {
      countValue: 0,
      currentValue: 0,
      isActive: false
    }
  },
  {
    display: {
      value: 5,
      isBankNote: false
    },
    displayCount: {
      countValue: 0,
      currentValue: 0,
      isActive: false
    }
  },
  {
    display: {
      value: 1,
      isBankNote: false
    },
    displayCount: {
      countValue: 0,
      currentValue: 0,
      isActive: false
    }
  }
];

export default function CashDenominationCounter({
  startValueObj,
  info,
  isResetObj,
  viaPaymentInfoObj,
  isCloseBalance = false,
  isCloseBalanceCfObg,
  onCashBreakdownChange = () => {},
  cashData = null
}) {
  const [sumValue, setSumValue] = useState('0.00');
  const [rowValues, setRowValues] = useState(initRowValues);
  const [isTestDataLoaded, setIsTestDataLoaded] = useState(false);

  const handleInputChange = (index, value) => {
    const updatedRowValues = [...rowValues];
    updatedRowValues[index].displayCount.currentValue = parseFloat(value) || 0;
    updatedRowValues[index].displayCount.countValue = (parseFloat(value) || 0) * updatedRowValues[index].display.value;
    updatedRowValues[index].displayCount.isActive = parseFloat(value) > 0;
    setRowValues(updatedRowValues);

    if (isCloseBalance) {
      setTimeout(() => handleCalculateButtonClick(), 0);
    }
  };

  const calculateRowSum = useCallback(() => {
    return rowValues.reduce((sum, item) => {
      const value = item.display.value || 0;
      const currentValue = item.displayCount.currentValue || 0;
      return sum + value * currentValue;
    }, 0);
  }, [rowValues]);

  useEffect(() => {
    const calculatedSum = calculateRowSum();
    setSumValue(calculatedSum);
    if (isCloseBalance) {
      startValueObj.setStartValue(calculatedSum);
    }
  }, [rowValues, calculateRowSum, startValueObj, isCloseBalance]);

  const handleCalculateButtonClick = useCallback(() => {
    if (!isCloseBalance) {
      startValueObj.setStartValue(sumValue);
    }

    const cashBreakdown = {
      cash_1000: rowValues[0].displayCount.currentValue,
      cash_500: rowValues[1].displayCount.currentValue,
      cash_100: rowValues[2].displayCount.currentValue,
      cash_50: rowValues[3].displayCount.currentValue,
      cash_20: rowValues[4].displayCount.currentValue,
      cash_10: rowValues[5].displayCount.currentValue,
      cash_5: rowValues[6].displayCount.currentValue,
      cash_1: rowValues[7].displayCount.currentValue
    };

    onCashBreakdownChange(cashBreakdown);
  }, [isCloseBalance, rowValues, onCashBreakdownChange, startValueObj, sumValue]);

  useEffect(() => {
    if (isResetObj && isResetObj.isReset) {
      const resetValues = JSON.parse(JSON.stringify(initRowValues));
      setRowValues(resetValues);

      if (startValueObj && startValueObj.setStartValue) {
        startValueObj.setStartValue('0.00');
      }

      if (isResetObj.setIsReset) {
        isResetObj.setIsReset(false);
      }
    }
  }, [isResetObj, startValueObj]);

  useEffect(() => {
    if (!isCloseBalance) return;

    const currentSumValueFloat = parseFloat(sumValue);
    const currentStartValueFloat = parseFloat(startValueObj.startValue || '0');

    if (Math.abs(currentSumValueFloat - currentStartValueFloat) > 0.001) {
      startValueObj.setStartValue(numberWithTwoDecimals(currentSumValueFloat));
    }

    if (viaPaymentInfoObj && viaPaymentInfoObj.viaPaymentInfo) {
      const newDifferenceAmount = currentSumValueFloat - viaPaymentInfoObj.viaPaymentInfo.cashAmount;
      const currentDifferenceAmount = viaPaymentInfoObj.viaPaymentInfo.differenceAmount || 0;

      if (Math.abs(newDifferenceAmount - currentDifferenceAmount) > 0.001) {
        viaPaymentInfoObj.setViaPaymentInfo(prevInfo => ({
          ...prevInfo,
          differenceAmount: newDifferenceAmount
        }));

        if (isCloseBalanceCfObg && isCloseBalanceCfObg.isCloseBalanceConfirm) {
          isCloseBalanceCfObg.setIsCloseBalanceConfirm(false);
        }
      }
    }
  }, [isCloseBalance, isCloseBalanceCfObg, startValueObj, sumValue, viaPaymentInfoObj]);

  useEffect(() => {
    if (isCloseBalance && !isTestDataLoaded) {
      const testRowValues = [
        {
          display: {
            value: 1000,
            isBankNote: true
          },
          displayCount: {
            countValue: 25000,
            currentValue: 0,
            isActive: true
          }
        },
        {
          display: {
            value: 500,
            isBankNote: true
          },
          displayCount: {
            countValue: 5000,
            currentValue: 0,
            isActive: true
          }
        },
        {
          display: {
            value: 100,
            isBankNote: true
          },
          displayCount: {
            countValue: 2500,
            currentValue: 0,
            isActive: true
          }
        },
        {
          display: {
            value: 50,
            isBankNote: false
          },
          displayCount: {
            countValue: 1000,
            currentValue: 0,
            isActive: true
          }
        },
        {
          display: {
            value: 20,
            isBankNote: false
          },
          displayCount: {
            countValue: 400,
            currentValue: 0,
            isActive: true
          }
        },
        {
          display: {
            value: 10,
            isBankNote: false
          },
          displayCount: {
            countValue: 450,
            currentValue: 0,
            isActive: true
          }
        },
        {
          display: {
            value: 5,
            isBankNote: false
          },
          displayCount: {
            countValue: 500,
            currentValue: 0,
            isActive: true
          }
        },
        {
          display: {
            value: 1,
            isBankNote: false
          },
          displayCount: {
            countValue: 100,
            currentValue: 0,
            isActive: true
          }
        }
      ];
      setRowValues(testRowValues);
      setIsTestDataLoaded(true);

      setTimeout(() => handleCalculateButtonClick(), 0);
    }
  }, [isCloseBalance, isTestDataLoaded, cashData, onCashBreakdownChange, handleCalculateButtonClick]);

  return (
    <div className="w-full">
      <div className="flex justify-between mt-[min(0.98vw,10px)]">
        <div className="w-[min(26.86vw,275px)]">
          <div className="font-[500] leading-[2] text-20">{info.cashierName}</div>
          <div className="bg-[#EDEDED] w-full h-[min(0.10vw,1px)]"></div>
          <div className="mt-[min(1.76vw,18px)] flex flex-col gap-[min(0.78vw,8px)] text-18">
            <div className="flex justify-between gap-[min(0.98vw,10px)] text-18">
              <div className="whitespace-nowrap text-[#474747]">ชื่อพนักงาน :</div>
              <div className="truncate">{info.staffName || '-'}</div>
            </div>
            <div className="flex justify-between gap-[min(0.98vw,10px)]  text-18">
              <div className="whitespace-nowrap text-[#474747]">เวลาเริ่มขาย :</div>
              <div className="truncate">{info.startTime || '-'}</div>
            </div>

            {!isCloseBalance && (
              <div className="flex items-center justify-between gap-[min(0.98vw,10px)]">
                <div className="whitespace-nowrap text-[#474747]">{info.operationDescription || 'เงินสดตั้งต้น'} :</div>
                <div className="w-[min(9.77vw,100px)]">
                  <CustomInput
                    inputprops={{
                      type: 'text',
                      readOnly: 'readOnly',
                      className: 'modal-input no-spinner text-18 disabled-input',
                      value: numberWithTwoDecimals(startValueObj.startValue),
                      min: 0,
                      onChange: e => {
                        const inputValue = parseFloat(e.currentTarget.value);
                        const roundedValue = Math.round(inputValue * 100) / 100;
                        startValueObj.setStartValue(roundedValue);
                      },
                      onClick: e => {
                        const input = e.target;
                        input.select();
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {isCloseBalance && (
              <>
                <div className="flex justify-between gap-[min(0.98vw,10px)]  text-18">
                  <div className="whitespace-nowrap text-[#474747]">
                    {info.operationDescription || 'เงินสดตั้งต้น'} :
                  </div>
                  <div className="text-[var(--color-green)] truncate">
                    {cashData
                      ? numberWithTwoDecimals(cashData.initial_cash)
                      : info.initialAmount !== undefined
                      ? numberWithTwoDecimals(info.initialAmount)
                      : '-'}
                  </div>
                </div>
                <div className="flex justify-between gap-[min(0.98vw,10px)]  text-18">
                  <div className="whitespace-nowrap text-[#474747]">นำเงินเข้ารวม :</div>
                  <div className="text-[var(--color-green)] truncate">
                    {cashData
                      ? numberWithTwoDecimals(cashData.total_cash_in)
                      : info.totalDepositAmount
                      ? numberWithTwoDecimals(info.totalDepositAmount)
                      : '-'}
                  </div>
                </div>
                <div className="flex justify-between gap-[min(0.98vw,10px)]  text-18">
                  <div className="whitespace-nowrap text-[#474747]">นำเงินออกรวม :</div>
                  <div className="text-[var(--color-red)] truncate">
                    {cashData
                      ? numberWithTwoDecimals(cashData.total_cash_out * -1)
                      : info.totalWithdrawalAmount
                      ? numberWithTwoDecimals(info.totalWithdrawalAmount * -1)
                      : '-'}
                  </div>
                </div>
                <div className="flex justify-between gap-[min(0.98vw,10px)]  text-18">
                  <div className="flex flex-col">
                    <div className="whitespace-nowrap text-[#474747]">ยอดขายหลังหักส่วนลด :</div>

                    <p className="text-10 mt[-10px] mb-2">รวม Service Charge </p>
                  </div>
                  <div
                    className={`${
                      cashData?.total_sales || info.grandTotalAmount ? 'text-[var(--color-green)]' : ''
                    } truncate`}
                  >
                    {cashData
                      ? numberWithTwoDecimals(cashData.total_sales)
                      : info.grandTotalAmount
                      ? numberWithTwoDecimals(info.grandTotalAmount)
                      : '-'}
                  </div>
                </div>

                <div className="flex justify-between gap-[min(0.98vw,10px)]  text-18">
                  <div className="whitespace-nowrap text-[#474747]">รายได้ค่าธรรมเนียมบัตร :</div>
                  <div
                    className={`${
                      cashData?.fee_revenue || info.feeIncomeAmount ? 'text-[var(--color-green)]' : ''
                    } truncate`}
                  >
                    {cashData
                      ? numberWithTwoDecimals(cashData.fee_revenue)
                      : info.feeIncomeAmount
                      ? numberWithTwoDecimals(info.feeIncomeAmount)
                      : '-'}
                  </div>
                </div>

                <div className="flex justify-between gap-[min(0.98vw,10px)]  text-18">
                  <div className="whitespace-nowrap text-[#474747]">คืนสินค้า :</div>
                  <div className={`${cashData?.sale_refund ? 'text-[var(--color-green)]' : ''} truncate`}>
                    {cashData
                      ? numberWithTwoDecimals(cashData.sale_refund)
                      : info.saleRefundAmount
                      ? numberWithTwoDecimals(info.saleRefundAmount)
                      : '-'}
                  </div>
                </div>

                <div className="flex justify-between gap-[min(0.98vw,10px)]  text-18">
                  <div className="whitespace-nowrap text-[#474747]">รวม :</div>
                  <div
                    className={`${
                      cashData?.total_amount || (info.grandTotalAmount && info.feeIncomeAmount)
                        ? 'text-[var(--color-green)]'
                        : ''
                    } truncate`}
                  >
                    {cashData
                      ? numberWithTwoDecimals(Number(cashData.total_amount || 0) - Number(cashData.sale_refund || 0))
                      : info.grandTotalAmount && info.feeIncomeAmount
                      ? numberWithTwoDecimals(info.grandTotalAmount + info.feeIncomeAmount)
                      : '-'}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="w-[min(22.07vw,226px)]">
          <div className="leading-[2] text-20">ตัวช่วยนับเงิน</div>
          <div className="bg-[#EDEDED] w-full h-[min(0.10vw,1px)]"></div>
          <div className="flex flex-col gap-[min(0.91vw,9.28px)] mt-[min(1.76vw,18px)]">
            {rowValues.map((value, index) => {
              return (
                <div key={index} className="flex justify-between">
                  <MoneyDisplay value={value.display.value} isBankNote={value.display.isBankNote} />
                  <MoneyDisplayCount
                    index={index}
                    currentValue={value.displayCount.currentValue}
                    countValue={value.displayCount.currentValue * value.display.value}
                    isActive={value.displayCount.currentValue * value.display.value > 0}
                    isCloseBalance={isCloseBalance}
                    handleInputChange={handleInputChange}
                  />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-[min(1.46vw,15px)]">
            <div className="text-18 text-[var(--color-grey-2)]">Total</div>
            <div className="w-[min(9.77vw,100px)] bg-[#F4F4F4] truncate flex justify-center items-center rounded-[min(0.20vw,2px)]">
              {numberWithTwoDecimals(sumValue)}
            </div>
            {!isCloseBalance && (
              <CustomBtn
                onClick={handleCalculateButtonClick}
                className={
                  'btn-main-active min-w-[min(6.74vw,69px)] text-white leading-[0.8] font-[400] text-16 pt-[min(1.27vw,13px)] pb-[min(0.78vw,8px)] rounded-[min(0.20vw,2px)]'
                }
                btntxt={info.operationType || 'คำนวณ'}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
